import { Activity, Period, SchoolDayConfig } from '../../types';

// Mock data for School Day page
// 5 periods, breaks, lunch, assembly times

export const mockPeriods: Period[] = [
  {
    id: 'period-1',
    ordinal: 1,
    periodNumber: 1,
    name: 'Period 1',
    startTime: '08:45',
    endTime: '09:45',
    duration: 60,
    isTeachingPeriod: true,
  },
  {
    id: 'period-2',
    ordinal: 2,
    periodNumber: 2,
    name: 'Period 2',
    startTime: '09:45',
    endTime: '10:45',
    duration: 60,
    isTeachingPeriod: true,
  },
  {
    id: 'period-3',
    ordinal: 3,
    periodNumber: 3,
    name: 'Period 3',
    startTime: '11:05',
    endTime: '12:05',
    duration: 60,
    isTeachingPeriod: true,
  },
  {
    id: 'period-4',
    ordinal: 4,
    periodNumber: 4,
    name: 'Period 4',
    startTime: '13:15',
    endTime: '14:15',
    duration: 60,
    isTeachingPeriod: true,
  },
  {
    id: 'period-5',
    ordinal: 5,
    periodNumber: 5,
    name: 'Period 5',
    startTime: '14:15',
    endTime: '15:15',
    duration: 60,
    isTeachingPeriod: true,
  },
];

export const mockActivities: Activity[] = [
  {
    id: 'activity-registration-am',
    name: 'Morning Registration',
    type: 'registration',
    startTime: '08:30',
    endTime: '08:45',
    daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  },
  {
    id: 'activity-assembly-mon',
    name: 'Monday Assembly',
    type: 'assembly',
    startTime: '08:45',
    endTime: '09:15',
    daysOfWeek: ['monday'],
  },
  {
    id: 'activity-break-morning',
    name: 'Morning Break',
    type: 'break',
    startTime: '10:45',
    endTime: '11:05',
    daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  },
  {
    id: 'activity-lunch',
    name: 'Lunch Break',
    type: 'lunch',
    startTime: '12:05',
    endTime: '13:15',
    daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  },
  {
    id: 'activity-registration-pm',
    name: 'Afternoon Registration',
    type: 'registration',
    startTime: '13:15',
    endTime: '13:20',
    daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  },
];

export const mockSchoolDayConfig: SchoolDayConfig = {
  startTime: '08:30',
  endTime: '15:15',
  periods: mockPeriods,
  activities: mockActivities,
};

// Helper function to get period by number
export const getPeriodByNumber = (periodNumber: number): Period | undefined => {
  return mockPeriods.find((p) => p.periodNumber === periodNumber);
};

// Helper function to get activities by type
export const getActivitiesByType = (type: Activity['type']): Activity[] => {
  return mockActivities.filter((a) => a.type === type);
};

// Helper function to calculate duration in minutes from time strings
const calculateDuration = (startTime: string, endTime: string): number => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  return endHour * 60 + endMin - (startHour * 60 + startMin);
};

// Helper function to get activities for a specific day
export const getActivitiesForDay = (day: string): Activity[] => {
  return mockActivities.filter((a) => a.daysOfWeek.includes(day as any));
};

// Timeline visualization data
export interface TimelineSlot {
  id: string;
  name: string;
  type: 'period' | 'activity';
  startTime: string;
  endTime: string;
  duration: number;
  color?: string;
}

export const generateTimelineSlots = (
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday',
): TimelineSlot[] => {
  const slots: TimelineSlot[] = [];

  // Add registration AM
  const regAM = mockActivities.find((a) => a.id === 'activity-registration-am');
  if (regAM) {
    slots.push({
      id: regAM.id,
      name: regAM.name,
      type: 'activity',
      startTime: regAM.startTime,
      endTime: regAM.endTime,
      duration: calculateDuration(regAM.startTime, regAM.endTime),
      color: '#e3f2fd',
    });
  }

  // Add assembly on Monday
  if (day === 'monday') {
    const assembly = mockActivities.find((a) => a.id === 'activity-assembly-mon');
    if (assembly) {
      slots.push({
        id: assembly.id,
        name: assembly.name,
        type: 'activity',
        startTime: assembly.startTime,
        endTime: assembly.endTime,
        duration: calculateDuration(assembly.startTime, assembly.endTime),
        color: '#fff3e0',
      });
    }
  } else {
    // Add Period 1 for non-Monday days
    const period1 = mockPeriods.find((p) => p.periodNumber === 1);
    if (period1) {
      slots.push({
        id: period1.id,
        name: `Period ${period1.periodNumber}`,
        type: 'period',
        startTime: period1.startTime,
        endTime: period1.endTime,
        duration: period1.duration,
        color: '#c8e6c9',
      });
    }
  }

  // Add Period 2
  const period2 = mockPeriods.find((p) => p.periodNumber === 2);
  if (period2) {
    slots.push({
      id: period2.id,
      name: `Period ${period2.periodNumber}`,
      type: 'period',
      startTime: period2.startTime,
      endTime: period2.endTime,
      duration: period2.duration,
      color: '#c8e6c9',
    });
  }

  // Add morning break
  const breakAM = mockActivities.find((a) => a.id === 'activity-break-morning');
  if (breakAM) {
    slots.push({
      id: breakAM.id,
      name: breakAM.name,
      type: 'activity',
      startTime: breakAM.startTime,
      endTime: breakAM.endTime,
      duration: calculateDuration(breakAM.startTime, breakAM.endTime),
      color: '#f3e5f5',
    });
  }

  // Add Period 3
  const period3 = mockPeriods.find((p) => p.periodNumber === 3);
  if (period3) {
    slots.push({
      id: period3.id,
      name: `Period ${period3.periodNumber}`,
      type: 'period',
      startTime: period3.startTime,
      endTime: period3.endTime,
      duration: period3.duration,
      color: '#c8e6c9',
    });
  }

  // Add lunch
  const lunch = mockActivities.find((a) => a.id === 'activity-lunch');
  if (lunch) {
    slots.push({
      id: lunch.id,
      name: lunch.name,
      type: 'activity',
      startTime: lunch.startTime,
      endTime: lunch.endTime,
      duration: calculateDuration(lunch.startTime, lunch.endTime),
      color: '#ffe082',
    });
  }

  // Add registration PM
  const regPM = mockActivities.find((a) => a.id === 'activity-registration-pm');
  if (regPM) {
    slots.push({
      id: regPM.id,
      name: regPM.name,
      type: 'activity',
      startTime: regPM.startTime,
      endTime: regPM.endTime,
      duration: calculateDuration(regPM.startTime, regPM.endTime),
      color: '#e3f2fd',
    });
  }

  // Add Period 4
  const period4 = mockPeriods.find((p) => p.periodNumber === 4);
  if (period4) {
    slots.push({
      id: period4.id,
      name: `Period ${period4.periodNumber}`,
      type: 'period',
      startTime: period4.startTime,
      endTime: period4.endTime,
      duration: period4.duration,
      color: '#c8e6c9',
    });
  }

  // Add Period 5
  const period5 = mockPeriods.find((p) => p.periodNumber === 5);
  if (period5) {
    slots.push({
      id: period5.id,
      name: `Period ${period5.periodNumber}`,
      type: 'period',
      startTime: period5.startTime,
      endTime: period5.endTime,
      duration: period5.duration,
      color: '#c8e6c9',
    });
  }

  return slots;
};
